import type { CoursePlan, PlanSession, PlanUnit } from '../types/coursePlan';

const STORAGE_KEY = 'sala_course_plans';

function canUseStorage(): boolean {
  return typeof localStorage !== 'undefined';
}

function readAll(): CoursePlan[] {
  if (!canUseStorage()) return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CoursePlan[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(plans: CoursePlan[]): void {
  if (!canUseStorage()) return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(plans));
}

export function createPlanUnitId(): string {
  return `unit_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createPlanSessionId(): string {
  return `session_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

export function createEmptyPlanUnit(order: number): PlanUnit {
  return {
    id: createPlanUnitId(),
    title: '',
    objective: '',
    sessions: [],
    order,
  };
}

export function createEmptyPlanSession(order: number): PlanSession {
  return {
    id: createPlanSessionId(),
    title: '',
    notes: '',
    order,
  };
}

export function getCoursePlan(courseId: string): CoursePlan {
  const existing = readAll().find((plan) => plan.courseId === courseId);
  if (existing) return existing;
  return { courseId, units: [], updatedAt: Date.now() };
}

export function saveCoursePlan(plan: CoursePlan): CoursePlan {
  const all = readAll();
  const saved: CoursePlan = {
    ...plan,
    updatedAt: Date.now(),
    units: [...plan.units]
      .sort((a, b) => a.order - b.order)
      .map((unit) => ({
        ...unit,
        sessions: [...unit.sessions].sort((a, b) => a.order - b.order),
      })),
  };
  const idx = all.findIndex((item) => item.courseId === saved.courseId);
  if (idx >= 0) all[idx] = saved;
  else all.push(saved);
  writeAll(all);
  return saved;
}

export function deleteCoursePlan(courseId: string): void {
  writeAll(readAll().filter((plan) => plan.courseId !== courseId));
}
